import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import dayjs from "dayjs";
import axiosInstance from "../../Api/axios.instance";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";

const emptyForm = {
  RegionId: "",
  NameOfSchool: "",
  Address: "",
  Registered: "",
  OwnerShipStatus: "",
  NoOfRooms: "",
  Landowner: "",
  ContactNo: "",
  Area: "",
  SquareYardsOrKanal: "",
  Status: "Active",
  CreatedDate: dayjs().format("YYYY-MM-DD"),
  DateClosed: "",
  ReasonForClosure: "",
  PinnedLocation: "",
  NoOfFloors: "",
  NoOfClasses: "",
  NoOfWashrooms: "",
  DateOfOwnership: "",
  IsSecondary: false,
  RentAmount: "",
};

export default function SchoolFormDialog({
  open,
  onClose,
  onSaved,
  clusters,
  editingSchool,
  defaultRegionId,
}) {
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const isEditing = Boolean(editingSchool);

  useEffect(() => {
    if (editingSchool) {
      const trimmedStatus = editingSchool.Status?.trim() || "Active";

      setFormData({
        ...emptyForm,
        ...editingSchool,
        Status: trimmedStatus,
        CreatedDate: editingSchool.DateCreated
          ? dayjs(editingSchool.DateCreated).format("YYYY-MM-DD")
          : "",
        DateClosed: editingSchool.DateClosed
          ? dayjs(editingSchool.DateClosed).format("YYYY-MM-DD")
          : "",
        DateOfOwnership: editingSchool.DateOfOwnership
          ? dayjs(editingSchool.DateOfOwnership).format("YYYY-MM-DD")
          : "",
      });
    } else {
      setFormData({
        ...emptyForm,
        RegionId: defaultRegionId || "",
      });
    }
  }, [editingSchool, open, defaultRegionId]);

  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleStatusChange = (e) => {
  const newStatus = e.target.value;
  setFormData((prev) => ({
    ...prev,
    Status: newStatus,
    DateClosed: newStatus === "Closed" ? prev.DateClosed : "",
    ReasonForClosure: newStatus === "Closed" ? prev.ReasonForClosure : "",
  }));
};

  const handleClear = () => setFormData(emptyForm);

  const handleDateOfOwnershipChange = (newValue) => {
    setFormData((prev) => ({
      ...prev,
      DateOfOwnership:
        newValue && dayjs(newValue).isValid()
          ? dayjs(newValue).format("YYYY-MM-DD")
          : "",
    }));
  };

  const handleClosedDateChange = (newValue) => {
    const formatted =
      newValue && dayjs(newValue).isValid()
        ? dayjs(newValue).format("YYYY-MM-DD")
        : "";
    setFormData((prev) => ({
      ...prev,
      DateClosed: formatted,
      Status: formatted
        ? "Closed"
        : prev.Status === "Closed"
          ? "Active"
          : prev.Status,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const UserId = JSON.parse(localStorage.User)?.username;
      const payload = { ...formData, DataEntryOperator: UserId };

      if (isEditing) {
        await axiosInstance.put(`/schools/${editingSchool.SchoolId}`, payload);
      } else {
        delete payload.DateClosed;
        delete payload.ReasonForClosure;
        await axiosInstance.post("/schools", payload);
      }
      if (onSaved) await onSaved();
      onClose();
    } catch (error) {
      console.log(error?.response?.data?.message || error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
      <DialogTitle>{isEditing ? "Edit School" : "Add New School"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Cluster"
              fullWidth
              size="small"
              name="RegionId"
              value={formData.RegionId || ""}
              onChange={handleChange}
              required
              disabled={!isEditing}
            >
              {(clusters || []).map((c) => (
                <MenuItem key={c.RegionId} value={c.RegionId}>
                  {c.RegionName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Name Of School"
              fullWidth
              size="small"
              name="NameOfSchool"
              value={formData.NameOfSchool || ""}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Address"
              fullWidth
              size="small"
              name="Address"
              value={formData.Address || ""}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Registered"
              fullWidth
              size="small"
              name="Registered"
              value={formData.Registered || ""}
              onChange={handleChange}
            >
              <MenuItem value="">Select</MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Ownership Status"
              fullWidth
              size="small"
              name="OwnerShipStatus"
              value={formData.OwnerShipStatus || ""}
              onChange={handleChange}
              helperText='e.g. "Rent", "SEW", "Donated Building"'
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Landowner"
              fullWidth
              size="small"
              name="Landowner"
              value={formData.Landowner || ""}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Contact No"
              fullWidth
              size="small"
              name="ContactNo"
              value={formData.ContactNo || ""}
              onChange={handleChange}
              inputProps={{ maxLength: 15 }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Area"
              fullWidth
              size="small"
              name="Area"
              value={formData.Area || ""}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Sq. Yards / Kanal"
              fullWidth
              size="small"
              name="SquareYardsOrKanal"
              value={formData.SquareYardsOrKanal || ""}
              onChange={handleChange}
            >
              <MenuItem value="">Select</MenuItem>
              <MenuItem value="Sq.Yards">Sq.Yards</MenuItem>
              <MenuItem value="Kanal">Kanal</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="No. of Rooms"
              type="number"
              fullWidth
              size="small"
              name="NoOfRooms"
              value={formData.NoOfRooms || ""}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="No. of Floors"
              type="number"
              fullWidth
              size="small"
              name="NoOfFloors"
              value={formData.NoOfFloors || ""}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="No. of Classes"
              type="number"
              fullWidth
              size="small"
              name="NoOfClasses"
              value={formData.NoOfClasses || ""}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="No. of Washrooms"
              type="number"
              fullWidth
              size="small"
              name="NoOfWashrooms"
              value={formData.NoOfWashrooms || ""}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Rent Amount"
              fullWidth
              size="small"
              name="RentAmount"
              value={formData.RentAmount || ""}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Secondary School"
              fullWidth
              size="small"
              name="IsSecondary"
              value={formData.IsSecondary ? "Yes" : "No"}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  IsSecondary: e.target.value === "Yes",
                }))
              }
            >
              <MenuItem value="No">No</MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Pinned Location"
              fullWidth
              size="small"
              name="PinnedLocation"
              value={formData.PinnedLocation || ""}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                label="Date of Ownership"
                inputFormat="DD-MM-YYYY"
                value={
                  formData.DateOfOwnership
                    ? dayjs(formData.DateOfOwnership)
                    : null
                }
                onChange={handleDateOfOwnershipChange}
                renderInput={(params) => (
                  <TextField fullWidth size="small" {...params} />
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Status"
              fullWidth
              size="small"
              name="Status"
              value={formData.Status || "Active"}
              onChange={handleStatusChange }
              disabled={!isEditing}
            >
              <MenuItem value="Active">Active</MenuItem>
              {isEditing && <MenuItem value="Inactive">Inactive</MenuItem>}
              {isEditing && <MenuItem value="Closed">Closed</MenuItem>}
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                label="Date Created"
                inputFormat="DD-MM-YYYY"
                value={
                  formData.CreatedDate ? dayjs(formData.CreatedDate) : null
                }
                onChange={(newValue) =>
                  setFormData((prev) => ({
                    ...prev,
                    CreatedDate:
                      newValue && dayjs(newValue).isValid()
                        ? dayjs(newValue).format("YYYY-MM-DD")
                        : "",
                  }))
                }
                disabled={isEditing}
                renderInput={(params) => (
                  <TextField fullWidth size="small" {...params} />
                )}
              />
            </LocalizationProvider>
          </Grid>

          {isEditing && (
            <>
              <Grid item xs={12} md={4}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MobileDatePicker
                    label="Date Closed"
                    inputFormat="DD-MM-YYYY"
                    value={
                      formData.DateClosed ? dayjs(formData.DateClosed) : null
                    }
                    onChange={handleClosedDateChange}
                    renderInput={(params) => (
                      <TextField fullWidth size="small" {...params} />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
              {formData.Status === "Closed" && (
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Reason For Closure"
                    fullWidth
                    size="small"
                    name="ReasonForClosure"
                    value={formData.ReasonForClosure || ""}
                    onChange={handleChange}
                  />
                </Grid>
              )}
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClear}>Clear</Button>
        <LoadingButton
          loading={saving}
          onClick={handleSave}
          variant="contained"
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
